import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import StatusChip from "@/components/StatusChip";
import CaseTimeline from "@/components/CaseTimeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inr, fmtDate, fmtDateTime } from "@/lib/constants";
import { toast } from "sonner";
import { Calendar, Video, FileText, UploadCloud, Send, Award, Gavel } from "lucide-react";

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [msg, setMsg] = useState("");
  const [neutrals, setNeutrals] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [chosenNeutral, setChosenNeutral] = useState("");
  const [showHearing, setShowHearing] = useState(false);
  const [hearingDate, setHearingDate] = useState("");
  const [showAward, setShowAward] = useState(false);
  const [awardForm, setAwardForm] = useState({ outcome: "settled", summary: "", amount: 0 });

  const load = useCallback(async () => {
    const { data } = await api.get(`/cases/${id}`);
    setC(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user.role === "admin") api.get("/neutrals").then(r => setNeutrals(r.data.filter(n => n.profile?.empanelment_status === "Active")));
  }, [user.role]);

  if (!c) return <div className="text-muted-foreground">Loading case…</div>;

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await api.post(`/cases/${id}/messages`, { body: msg });
      setMsg("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onUpload = async (e) => {
    const fs = Array.from(e.target.files || []);
    if (!fs.length) return;
    for (const f of fs) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/cases/${id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    toast.success("Evidence uploaded");
    load();
  };

  const assign = async () => {
    if (!chosenNeutral) return toast.error("Pick a mediator/arbitrator");
    try {
      await api.post(`/cases/${id}/assign`, { neutral_id: chosenNeutral });
      toast.success("Mediator/Arbitrator assigned");
      setShowAssign(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const schedule = async () => {
    if (!hearingDate) return toast.error("Pick a date/time");
    try {
      await api.post(`/cases/${id}/hearings`, { scheduled_at: new Date(hearingDate).toISOString(), mode: "video", participants: [c.claimant_id, c.assigned_neutral_id].filter(Boolean) });
      toast.success("Hearing scheduled");
      setShowHearing(false);
      setHearingDate("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const issueAward = async () => {
    if (!awardForm.summary) return toast.error("Add a summary");
    try {
      await api.post(`/cases/${id}/award`, awardForm);
      toast.success("Award issued — case resolved");
      setShowAward(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const advance = async (status) => {
    try {
      await api.post(`/cases/${id}/status?status=${encodeURIComponent(status)}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="case-detail">
      {/* Header */}
      <div className="bg-card rounded-sm border border-stone/60 p-6 lg:p-8 ">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs text-muted-foreground font-mono">{c.case_number}</div>
            <h1 className="font-display text-3xl font-bold text-court mt-1">{c.title}</h1>
            <div className="text-sm text-muted-foreground mt-1 capitalize">{c.type} · filed {fmtDate(c.created_at)} · {c.resolution_method}</div>
          </div>
          <div className="flex items-center gap-3">
            <StatusChip status={c.status} />
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Claim</div>
              <div className="font-display text-2xl font-semibold text-court">{inr(c.claim_amount)}</div>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 mt-6 text-sm">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Claimant</div>
            <div className="font-medium mt-1">{c.claimant?.name || "—"}</div>
            <div className="text-xs text-muted-foreground">{c.claimant?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Respondent</div>
            <div className="font-medium mt-1">{c.respondent_name}</div>
            <div className="text-xs text-muted-foreground">{c.respondent_contact}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Assigned mediator/arbitrator</div>
            <div className="font-medium mt-1">{c.assigned_neutral?.name || "Unassigned"}</div>
            <div className="text-xs text-muted-foreground">SLA due {fmtDate(c.sla_due_date)}</div>
          </div>
        </div>

        {/* Role-specific actions */}
        <div className="flex flex-wrap gap-2 mt-6">
          {user.role === "admin" && (
            <>
              <Dialog open={showAssign} onOpenChange={setShowAssign}>
                <DialogTrigger asChild><Button data-testid="action-assign-neutral" className="rounded-sm bg-primary"><Gavel className="h-4 w-4 mr-2" />Assign mediator/arbitrator</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Assign a mediator or arbitrator</DialogTitle></DialogHeader>
                  <Select value={chosenNeutral} onValueChange={setChosenNeutral}>
                    <SelectTrigger data-testid="assign-neutral-select"><SelectValue placeholder="Pick from active panel" /></SelectTrigger>
                    <SelectContent>
                      {neutrals.map(n => <SelectItem key={n.id} value={n.id}>{n.name} — {n.profile?.specializations?.join(", ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <DialogFooter><Button onClick={assign} data-testid="assign-neutral-confirm" className="rounded-sm bg-primary">Assign</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Button variant="outline" className="rounded-sm" data-testid="action-under-review" onClick={() => advance("Under Review")}>Mark Under Review</Button>
              <Button variant="outline" className="rounded-sm" data-testid="action-notice-sent" onClick={() => advance("Notice Sent")}>Notice Sent</Button>
              <Button variant="outline" className="rounded-sm" data-testid="action-close" onClick={() => advance("Closed")}>Close case</Button>
            </>
          )}
          {(user.role === "neutral" || user.role === "admin") && (
            <>
              <Dialog open={showHearing} onOpenChange={setShowHearing}>
                <DialogTrigger asChild><Button variant="outline" className="rounded-sm" data-testid="action-schedule-hearing"><Calendar className="h-4 w-4 mr-2" />Schedule hearing</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Schedule a hearing</DialogTitle></DialogHeader>
                  <Input type="datetime-local" value={hearingDate} onChange={(e) => setHearingDate(e.target.value)} data-testid="hearing-datetime" />
                  <DialogFooter><Button onClick={schedule} className="rounded-sm bg-primary" data-testid="hearing-confirm">Schedule</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={showAward} onOpenChange={setShowAward}>
                <DialogTrigger asChild><Button className="rounded-sm bg-secondary" data-testid="action-issue-award"><Award className="h-4 w-4 mr-2" />Issue outcome</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Issue outcome / award</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Outcome</Label>
                      <Select value={awardForm.outcome} onValueChange={(v) => setAwardForm(f => ({...f, outcome: v}))}>
                        <SelectTrigger data-testid="award-outcome"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="settled">Settled (mediation)</SelectItem>
                          <SelectItem value="award_issued">Award issued (arbitration)</SelectItem>
                          <SelectItem value="withdrawn">Withdrawn</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Amount payable (₹)</Label><Input data-testid="award-amount" type="number" value={awardForm.amount} onChange={(e) => setAwardForm(f => ({...f, amount: Number(e.target.value)}))} /></div>
                    <div><Label>Summary / reasoning</Label><Textarea data-testid="award-summary" value={awardForm.summary} onChange={(e) => setAwardForm(f => ({...f, summary: e.target.value}))} className="min-h-[120px]" /></div>
                  </div>
                  <DialogFooter><Button onClick={issueAward} className="rounded-sm bg-primary" data-testid="award-confirm">Issue &amp; close</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-card border border-stone/60 rounded-sm p-1">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-court-foreground">Overview</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-court-foreground">Evidence ({c.evidence.length})</TabsTrigger>
          <TabsTrigger value="hearings" data-testid="tab-hearings" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-court-foreground">Hearings ({c.hearings.length})</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-court-foreground">Messages</TabsTrigger>
          {c.award && <TabsTrigger value="award" data-testid="tab-award" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-court-foreground">Award</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="bg-card rounded-sm border border-stone/60 p-6 lg:p-8 ">
            <h4 className="font-display text-lg text-court mb-2">Description</h4>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{c.description}</p>
            <div className="mt-8"><CaseTimeline currentStatus={c.status} events={c.timeline || []} /></div>
          </div>
        </TabsContent>

        <TabsContent value="evidence" className="mt-4">
          <div className="bg-card rounded-sm border border-stone/60 p-6 lg:p-8 ">
            <label className="block border-2 border-dashed border-stone/60 rounded-sm p-8 text-center cursor-pointer hover:border-primary/40 transition mb-6" data-testid="evidence-upload">
              <UploadCloud className="h-7 w-7 text-court mx-auto mb-1" />
              <div className="font-medium">Add more evidence</div>
              <div className="text-xs text-muted-foreground">Files are auto-classified</div>
              <input type="file" multiple className="hidden" onChange={onUpload} data-testid="evidence-input" />
            </label>
            {c.evidence.length === 0 ? <div className="text-muted-foreground">No evidence uploaded yet.</div> : (
              <ul className="grid sm:grid-cols-2 gap-3">
                {c.evidence.map(ev => (
                  <li key={ev.id} className="rounded-sm border border-stone/60 p-4 flex gap-3 items-center" data-testid={`evidence-${ev.id}`}>
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-court"><FileText className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{ev.file_name}</div>
                      <div className="text-xs text-muted-foreground">{ev.doc_type} · {Math.round(ev.file_size/1024)} KB · uploaded {fmtDate(ev.uploaded_at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="hearings" className="mt-4">
          <div className="bg-card rounded-sm border border-stone/60 p-6 lg:p-8 ">
            {c.hearings.length === 0 ? <div className="text-muted-foreground">No hearings scheduled yet.</div> : (
              <ul className="space-y-3">
                {c.hearings.map(h => (
                  <li key={h.id} className="rounded-sm border border-stone/60 p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`hearing-${h.id}`}>
                    <div>
                      <div className="font-semibold">{fmtDateTime(h.scheduled_at)}</div>
                      <div className="text-xs text-muted-foreground">Mode: {h.mode} · Status: {h.status}</div>
                      {h.notes && <div className="text-sm text-muted-foreground mt-1">{h.notes}</div>}
                    </div>
                    <a href={h.video_room_url} target="_blank" rel="noreferrer" data-testid={`join-hearing-${h.id}`}>
                      <Button className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/90"><Video className="h-4 w-4 mr-2" />Join hearing</Button>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="messages" className="mt-4">
          <div className="bg-card rounded-sm border border-stone/60 p-6 lg:p-8 ">
            <ul className="space-y-3 max-h-[400px] overflow-auto pr-2 mb-4">
              {c.messages.length === 0 && <li className="text-muted-foreground">No messages yet.</li>}
              {c.messages.map(m => (
                <li key={m.id} className="rounded-sm bg-muted/40 p-3" data-testid={`msg-${m.id}`}>
                  <div className="text-xs text-muted-foreground">{m.sender?.name} · {m.sender?.role} · {fmtDateTime(m.created_at)}</div>
                  <div className="text-sm mt-1 whitespace-pre-line">{m.body}</div>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" className="h-12 rounded-sm" data-testid="message-input" onKeyDown={(e) => e.key === "Enter" && sendMsg()} />
              <Button onClick={sendMsg} className="rounded-sm bg-primary" data-testid="message-send"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        </TabsContent>

        {c.award && (
          <TabsContent value="award" className="mt-4">
            <div className="bg-card rounded-sm border border-stone/60 p-6 lg:p-8 " data-testid="award-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-sm bg-emerald-50 text-emerald-700 grid place-items-center"><Award className="h-6 w-6" /></div>
                <div>
                  <h3 className="font-display text-2xl text-court capitalize">{c.award.outcome.replace("_", " ")}</h3>
                  <div className="text-sm text-muted-foreground">Issued {fmtDateTime(c.award.issued_at)}</div>
                </div>
              </div>
              <div className="rounded-sm bg-muted/40 p-5">
                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Amount</div>
                <div className="font-display text-3xl font-semibold text-court">{inr(c.award.amount)}</div>
                <div className="mt-4 text-sm whitespace-pre-line">{c.award.summary}</div>
              </div>
              <Button className="mt-5 rounded-sm bg-primary" onClick={() => window.print()} data-testid="award-download">Download as PDF</Button>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
